import { memo, useEffect, useMemo, useState } from 'react';
import { CircularProgress, Grid } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';

import DateSelectBox from './DateSelectBox';

import useAxiosPrivate from 'hooks/useAxiosPrivate';
import DoughnutChart from 'components/ui/charts/DoughnutChart';
import CurrencyFormat from 'components/ui/text-formats/CurrencyFormat';
import List from './List';

const ProductStatistics = ({ setData }) => {
  const axiosPrivate = useAxiosPrivate();
  const [searchParams] = useSearchParams();
  const [hasError, setHasError] = useState(false);
  const [isTotal, setIsTotal] = useState(false);

  const [startAndFinishDate, setStartAndFinishDate] = useState({
    startDate: '',
    finishDate: '',
  });
  const objectId = searchParams.get('objectId');

  const search = `${
    startAndFinishDate.startDate ? '?from=' + startAndFinishDate.startDate : ''
  }${
    startAndFinishDate.finishDate
      ? '&till=' + startAndFinishDate.finishDate
      : ''
  }`;

  const { isLoading, isFetching, isError, data } = useQuery({
    queryKey: [
      '/analytic/object/product/{objectId}',
      startAndFinishDate,
      objectId,
      search,
    ],
    queryFn: async function () {
      const response = await axiosPrivate.get(
        `/analytic/object/product/${objectId}${search}`
      );
      return response.data.data;
    },

    enabled: !!objectId && !hasError,
    onError: (error) => {
      setHasError(true);
    },
    retry: false,
  });

  function getRandomColor() {
    var letters = '0123456789ABCDEF';
    var color = '#';
    for (var i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  }

  const square = useMemo(() => {
    if (data?.blocks?.length)
      return data?.blocks?.reduce((a, c) => a + +c?.square, 0);
    return 0;
  }, [data]);

  const coloredPipes = useMemo(() => {
    if (data?.blocks?.length && square) {
      const inval = data?.object?.invalute / square;
      const val = data?.object?.totalval / square;
      const sum = data?.object?.totalsum / square;

      const bySquare = data?.blocks?.map((p) => ({
        ...p,
        totalvalBySquare: p?.totalval / square,
        totalsumBySquare: p?.totalsum / square,
        invaluteBySquare: p?.invalute / square,
        totalvalBySquareAll: p?.totalval / square + val,
        totalsumBySquareAll: p?.totalsum / square + sum,
        invaluteBySquareAll: p?.invalute / square + inval,
        invaluteAll: p?.invalute + inval * p?.square,
        color: getRandomColor(),
      }));
      return bySquare?.sort?.(
        (a, b) => b?.invaluteBySquare - a?.invaluteBySquare
      );
    }
    return [];
  }, [data, square]);

  useEffect(() => {
    if (data?.object) {
      const sum = data?.blocks?.reduce((a, c) => a + c?.totalsum, 0);
      const val = data?.blocks?.reduce((a, c) => a + c?.totalval, 0);
      const invalute = data?.blocks?.reduce((a, c) => a + c?.invalute, 0);

      setData((pr) => ({
        ...pr,
        square,
        product: { object: data?.object, sum, val, invalute },
        isLoading: pr?.isLoading || isLoading,
      }));
    }
  }, [square, data, isLoading]);

  const datalist = useMemo(() => {
    if (coloredPipes?.length) {
      if (isTotal) return coloredPipes?.map((p) => p?.invaluteBySquareAll);
      return coloredPipes?.map((p) => p?.invaluteBySquare);
    }
    return [];
  }, [isTotal, coloredPipes]);

  return (
    <div className='residential-complex-item income-by-residential-complex !shadow-md !base-border'>
      <div className='residential-complex-item-header'>
        <div className='residential-complex-item-title font-semibold text-lg income-by-residential-complex-title'>
          Mahsulot sarfi
        </div>
        <div className='w-[150px]'>
          <DateSelectBox
            setStartAndFinishDate={setStartAndFinishDate}
            disabled={isLoading || isFetching}
          />
        </div>
      </div>

      <Grid container spacing={2} columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
        <Grid item={true} lg={2} md={2} sm={12} xs={12}>
          <div className='shadow-md p-2 border rounded-md w-full h-full'>
            <div className='font-semibold text-center'>
              Obyekt uchun mahsulot sarfi m<sup>2</sup> kesimi bo'yicha
            </div>
            <div className='p-2 border shadow-md mt-2'>
              UZS:{' '}
              <CurrencyFormat
                value={data?.object?.totalsum / square}
                suffix=''
                decimalScale={2}
              />
            </div>
            <div className='p-2 border shadow-md mt-2'>
              USD:{' '}
              <CurrencyFormat
                value={data?.object?.totalval / square}
                suffix=''
                decimalScale={2}
              />
            </div>
            <div className='p-2 border shadow-md mt-2'>
              Jami USD:{' '}
              <CurrencyFormat
                value={data?.object?.invalute / square}
                suffix=''
                decimalScale={2}
              />
            </div>
          </div>
        </Grid>
        <Grid item={true} lg={5} md={5} sm={12} xs={12}>
          <div className='shadow-md p-2 border rounded-md w-full h-full'>
            <div className='font-semibold text-center'>
              m<sup>2</sup> kesimida bloklar uchun mahsulot sarfi
              <label>
                <input
                  checked={isTotal}
                  onChange={(e) => setIsTotal(e.target.checked)}
                  type='checkbox'
                  className='mx-2'
                />
                <span className=''>Obyekt sarfi bilan</span>
              </label>
            </div>
            <div className='income-by-residential-body'>
              {isError ? (
                <div className='no-data-found-wrapper h-full'>
                  <i className='bi bi-exclamation-octagon text-lg mr-1' />{' '}
                  Ma'lumot topilmadi!
                </div>
              ) : coloredPipes.length > 0 ? (
                <div className='flex'>
                  <div className='income-by-residential-chart'>
                    <DoughnutChart
                      style={{ width: '100px' }}
                      chartLabels={coloredPipes?.map((p) => p?.name)}
                      chartDatasets={[
                        {
                          data: datalist,
                          backgroundColor: coloredPipes?.map((p) => p?.color),
                          borderWidth: 2,
                          hoverOffset: 15,
                          hoverBorderWidth: 0,
                          borderRadius: 4,
                        },
                      ]}
                      suffix=' $'
                      optionPlugins={{
                        legend: {
                          display: false,
                        },
                      }}
                      options={{
                        layout: {
                          padding: {
                            top: 10,
                            bottom: 10,
                          },
                        },
                        parsing: {
                          key: 'total',
                        },
                      }}
                    />
                  </div>
                  <List
                    list={coloredPipes}
                    field={isTotal ? 'invaluteBySquareAll' : 'invaluteBySquare'}
                  />
                </div>
              ) : (
                <div className='h-[260px] flex items-center justify-center'>
                  <CircularProgress size={30} color='inherit' />
                </div>
              )}
            </div>
          </div>
        </Grid>
        <Grid item={true} lg={5} md={5} sm={12} xs={12}>
          <div className='shadow-md p-2 border rounded-md w-full h-full'>
            <div className='font-semibold text-center'>
              Umumiy mahsulot sarfi obyekt va bloklar uchun
            </div>
            <div className='income-by-residential-body'>
              {isError ? (
                <div className='no-data-found-wrapper h-full'>
                  <i className='bi bi-exclamation-octagon text-lg mr-1' />{' '}
                  Ma'lumot topilmadi!
                </div>
              ) : coloredPipes.length > 0 ? (
                <div className='flex'>
                  <div className='income-by-residential-chart'>
                    <DoughnutChart
                      chartLabels={coloredPipes?.map((p) => p?.name)}
                      chartDatasets={[
                        {
                          data: coloredPipes?.map((p) => p?.invaluteAll),
                          backgroundColor: coloredPipes?.map((p) => p?.color),
                          borderWidth: 2,
                          hoverOffset: 15,
                          hoverBorderWidth: 0,
                          borderRadius: 4,
                        },
                      ]}
                      suffix=' $'
                      optionPlugins={{
                        legend: {
                          display: false,
                        },
                      }}
                      options={{
                        layout: {
                          padding: {
                            top: 10,
                            bottom: 10,
                          },
                        },
                        parsing: {
                          key: 'total',
                        },
                      }}
                    />
                  </div>
                  <List
                    list={coloredPipes}
                    field='invaluteAll'
                    decimalScale={1}
                  />
                </div>
              ) : (
                <div className='h-[260px] flex items-center justify-center'>
                  <CircularProgress size={30} color='inherit' />
                </div>
              )}
            </div>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default memo(ProductStatistics);
